import styled, { keyframes } from 'styled-components'
import variables from '../../styles/variables'

const fadeIn = keyframes`
  from { 
    opacity: 0; 
    transform: translateY(10px); 
  }
  to { 
    opacity: 1; 
    transform: translateY(0); 
  }
`

export const CarouselContainer = styled.div`
  position: relative;
  padding: 4rem 0;
  background: ${variables.colors.white};
  
  &:focus {
    outline: none;
  }
  
  &:focus-visible {
    outline: 2px solid ${variables.colors.primary};
    outline-offset: 2px;
  }
`

export const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`

export const SectionTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  color: ${variables.colors.deepIndigo};
  margin: 0 0 1rem 0;
  font-family: ${variables.fonts.header};
  
  @media ${variables.media.sm} {
    font-size: 2rem;
  }
`

export const SectionSubtitle = styled.p`
  font-size: 1.2rem;
  color: ${variables.colors.greyDark};
  margin: 0;
  max-width: 600px;
  margin: 0 auto;
  
  @media ${variables.media.sm} {
    font-size: 1.1rem;
  }
`

export const CarouselContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  
  @media ${variables.media.sm} {
    padding: 0 1rem;
  }
`

export const CarouselCard = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.2fr;
  gap: 3rem;
  align-items: center;
  animation: ${fadeIn} 0.5s ease-out;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`

export const CardContent = styled.div`
  padding: 0;
`

export const CardLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`

export const CardRight = styled.div`
  position: relative;
  height: 400px;
  overflow: hidden;
  border-radius: 16px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
  background: ${variables.colors.greyLightest};
  
  @media (max-width: 1024px) {
    height: 350px;
  }
  
  @media (max-width: 768px) {
    height: 300px;
  }
  
  @media (max-width: 480px) {
    height: 250px;
  }
`

export const CardHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

export const CardTitle = styled.h3`
  font-size: 2.25rem;
  font-weight: 700;
  color: ${variables.colors.deepIndigo};
  margin: 0;
  font-family: ${variables.fonts.header};
  line-height: 1.2;
  
  @media ${variables.media.sm} {
    font-size: 1.75rem;
  }
`

export const MetricBadge = styled.div`
  display: inline-flex;
  align-items: baseline;
  gap: 0.5rem;
  background: linear-gradient(135deg, ${variables.colors.sunsetPurple} 0%, ${variables.colors.primary} 100%);
  color: ${variables.colors.white};
  padding: 0.5rem 1.5rem;
  border-radius: 24px;
  font-weight: 600;
  box-shadow: 0 4px 16px rgba(155, 105, 255, 0.3);
  align-self: flex-start;
`

export const MetricValue = styled.span`
  font-size: 1.25rem;
  font-weight: 700;
`

export const MetricLabel = styled.span`
  font-size: 0.875rem;
  opacity: 0.9;
`

export const CodeSection = styled.div`
  position: relative;
  background: linear-gradient(135deg, 
    ${variables.colors.deepIndigo} 0%, 
    ${variables.colors.greyDarkest} 100%);
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 40px;
    background: rgba(0, 0, 0, 0.2);
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }
  
  &::after {
    content: 'AITO QUERY';
    position: absolute;
    top: 12px;
    left: 20px;
    font-size: 0.75rem;
    color: rgba(255, 255, 255, 0.6);
    letter-spacing: 1px;
    font-weight: 600;
  }
  
  @media ${variables.media.sm} {
    padding: 1.25rem;
  }
`

export const CodeTeaser = styled.code`
  display: block;
  font-family: ${variables.fonts.mono};
  font-size: 1.1rem;
  color: ${variables.colors.turquoiseAccent};
  line-height: 1.6;
  margin-top: 2.5rem;
  white-space: pre-wrap;
  word-break: break-all;
  
  @media ${variables.media.sm} {
    font-size: 0.95rem;
  }
`

export const CopyButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.8);
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  z-index: 2;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    color: ${variables.colors.white};
    transform: scale(1.05);
  }
  
  &:focus {
    outline: none;
    color: ${variables.colors.white};
  }
  
  &:focus-visible {
    outline: 2px solid ${variables.colors.primary};
    outline-offset: 2px;
  }
`


export const CopiedTooltip = styled.span`
  position: absolute;
  bottom: 100%;
  right: 50%;
  transform: translateX(50%);
  background: ${variables.colors.greyDarkest};
  color: ${variables.colors.white};
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  white-space: nowrap;
  margin-bottom: 0.5rem;
  animation: ${fadeIn} 0.2s ease-out;
  
  &::after {
    content: '';
    position: absolute;
    top: 100%;
    right: 50%;
    transform: translateX(50%);
    border: 4px solid transparent;
    border-top-color: ${variables.colors.greyDarkest};
  }
`

export const CardImage = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: top center;
  }
  
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, 
      transparent 0%, 
      transparent 70%, 
      rgba(0, 0, 0, 0.05) 100%);
    pointer-events: none;
  }
`

export const CardFooter = styled.div`
  display: flex;
  justify-content: flex-start;
  margin-top: 1rem;
`

export const DemoLink = styled.a`
  background: linear-gradient(135deg, ${variables.colors.primary} 0%, ${variables.colors.turquoiseAccent} 100%);
  color: ${variables.colors.white};
  font-weight: 600;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border-radius: 24px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 16px rgba(18, 181, 173, 0.3);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(18, 181, 173, 0.4);
  }
  
  &:focus {
    outline: none;
  }
  
  &:focus-visible {
    outline: 2px solid ${variables.colors.primary};
    outline-offset: 2px;
  }
`

export const CarouselControls = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 3rem;
`

export const DotsContainer = styled.div`
  display: flex;
  gap: 1rem;
`

export const Dot = styled.button`
  width: ${props => props.active ? '48px' : '16px'};
  height: 16px;
  border-radius: 8px;
  border: none;
  background: ${props => props.active 
    ? `linear-gradient(135deg, ${variables.colors.sunsetPurple} 0%, ${variables.colors.primary} 100%)` 
    : variables.colors.greyLight};
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  padding: 0;
  box-shadow: ${props => props.active ? '0 4px 16px rgba(155, 105, 255, 0.3)' : 'none'};
  
  &:hover {
    background: ${props => props.active 
      ? `linear-gradient(135deg, ${variables.colors.sunsetPurple} 0%, ${variables.colors.primary} 100%)` 
      : variables.colors.grey};
    transform: translateY(-1px);
  }
  
  &:focus {
    outline: none;
  }
  
  &:focus-visible {
    outline: 2px solid ${variables.colors.primary};
    outline-offset: 2px;
  }
`